import * as React from 'react';
import { Box, Typography, Stack, Tooltip, Skeleton } from '@mui/material';
import Sparkline from '../Sparkline'; // you already have this
import { FS, SP, KPI_LABEL_SX, focusRing, hoverOnly } from '../../theme/tokens';
import { v2Tone } from '../../theme/theme';

/**
 * StatTile — single KPI value with optional label, sparkline, and click-to-drill.
 *
 * @description
 *   Renders one labelled metric. When `onClick` is provided the value becomes an
 *   interactive control: it reads as a button to assistive tech (role/tabIndex/
 *   keyboard) and shows a persistent dotted-underline affordance so it looks
 *   clickable at rest (not only on hover). Non-onClick cells render as plain text.
 *
 *   🔴 RENAMED 2026-08-05 (Owner-Entscheid E1), `KpiStat` → `StatTile`. The
 *   P2b props contract (`2026-08-05-p2b-inbox-spec-delta.md` §2.5) names this
 *   baustein `StatTile`, and P2b is the first real consumer. There is NO
 *   transition re-export from the old name: all 19 importers moved in the same
 *   commit, because a compatibility shim without an expiry date is a permanent
 *   second name, and two names for one component is the drift the whole v2
 *   token/baustein exercise exists to stop. 19 import lines were cheaper than
 *   owning a shim.
 *
 *   🔴 `hint` CHANGED MEANING in the same commit, and this is the one rename
 *   here that is not merely cosmetic. It used to be a tooltip on the LABEL; the
 *   contract uses `hint` for the optional THIRD LINE under the value. So:
 *     - `hint`         → the third line (what `secondary` used to be)
 *     - `labelTooltip` → the old `hint`, the tooltip on the label
 *     - `secondary`    → GONE. It was the same third line under a second name,
 *                        and it rendered as `variant="caption"
 *                        color="text.secondary"` with no v2 treatment at all.
 *                        All 37 call sites moved to `hint`.
 *   Because the meaning flipped rather than the name, a missed call site would
 *   have turned a hover tooltip into a permanently visible line of body text.
 *   Every `hint=` occurrence was therefore migrated by a codemod scoped to
 *   `<KpiStat …>` tag spans, not by a file-wide replace — two files in the repo
 *   (`home/HomeDashboard.jsx`, `pages/MediaLibrary/MediaUploadModal.jsx`) carry
 *   a `hint=` prop on OTHER components and must not move.
 *
 * @useWhen
 *   - A compact metric inside a KpiRow / KpiCell
 *   - A metric that drills into a filtered table or a detail modal (pass onClick)
 *   - A metric with a trend chip / icon in the corner (pass `right`)
 *
 * @dontUseFor
 *   - Rich multi-line cards — use a DashboardKit card instead
 *   - Standalone navigation buttons — use MUI Button
 *
 * @related
 *   - KpiRow / KpiCell — the container that lays out multiple StatTile side-by-side
 *   - Sparkline — the trend row this renders in its `series` slot
 *   - Pill — for the `right` corner slot when the trend is a chip, not an icon
 *
 * @reuseHints
 *   - Any surface that currently hand-rolls "label over number": the PO
 *     dashboard, vendor report headers, market-lab summary strips
 *   - `scale="v2"` is the ONE Design-Language v2 stat tile — do not build a
 *     third one (registry, 2026-08-05)
 *   - `loading` + `reserveSeries` make it usable for any tile whose numbers
 *     arrive on a separate fetch from the surface around it
 *   - `tone` + `valueColor` cover semantic vs. bespoke colouring, so a caller
 *     needing a coloured KPI never needs to fork the component
 *
 * @props
 *  - label: string
 *  - value: string | number
 *  - hint?: string — the optional THIRD LINE under the value (contract §2.5).
 *      Under `scale="v2"` it renders at `ink70`, the canon's level for
 *      supporting-but-load-bearing text; under the default scale it keeps the
 *      old `variant="caption" color="text.secondary"` so the 104 existing call
 *      sites do not change appearance. See the 🔴 note above — this prop used
 *      to be the label tooltip.
 *  - series?: number[]
 *  - right?: ReactNode — corner slot (trend chip, icon)
 *  - labelTooltip?: string — tooltip on the label (this is the old `hint`)
 *  - onClick?: () => void — makes the value an accessible button (drill-in)
 *  - ariaLabel?: string — aria-label for the clickable value (defaults to label).
 *      Strongly recommended when onClick is set so screen readers announce intent.
 *  - valueColor?: string — explicit override; WINS over `tone`
 *  - scale?: 'default' | 'v2' — 'v2' switches to the Design-Language v2
 *      anatomy: uppercase label with .06em tracking at ink-45, value in
 *      Plus Jakarta Sans 800 at FS.display, tabular figures. Same component,
 *      not a second tile — the P1 plan proposed a SECOND component for this on
 *      the false premise that label-over-value was new; it is what this file
 *      has always done. Opt-in, so the 104 existing call sites (18 files) are
 *      untouched. Sparkline, drill-in, `right` and `hint` all still work.
 *  - tone?: 'plain' | 'ok' | 'warn' | 'err' | 'info' — semantic value colour
 *      under `scale="v2"`. Ignored when `valueColor` is set. The neutral value
 *      is `'plain'`, matching the contract and `Pill`'s tone vocabulary; it was
 *      `'default'` until 2026-08-05, which was the only tone name in the v2
 *      baustein set that did not line up with the others. `'info'` is additive
 *      beyond the contract's four — removing a working tone to match a list
 *      would be a regression, not a correction.
 *  - loading?: boolean — the tile's OWN loading form. Skeleton, never a
 *      spinner (repo convention). The KPI numbers come from their own fetch
 *      (`rma/hooks/useRmaKpis.js`) and the inbox spec says the tiles
 *      deliberately do NOT skeletonise with the list, so without a loading
 *      form of its own a tile either flashes empty or prints a false zero.
 *      The LABEL is not skeletonised — it is a static prop, not fetched, and
 *      showing it immediately is both honest and one less layout shift. Only
 *      the fetched parts (value, hint, sparkline) become blocks.
 *      `onClick` is not wired while loading: a skeleton is not a drill-in.
 *  - reserveSeries?: boolean — declares that this tile HAS a sparkline slot,
 *      independent of whether the data has arrived. Needed because while
 *      loading there is no data to infer from: the real caller passes
 *      `series={kpis.open?.series}` and `kpis` is `null` until the fetch
 *      lands, so a loading branch keyed on `series` reserves nothing and the
 *      tile grows ~38px on arrival — cancelling the reason the label is not
 *      skeletonised. Keying it on `series={[]}` only inverts the jump into a
 *      shrink. Default reproduces the loaded branch's own condition
 *      (`series` is a non-empty array), so every existing call site is
 *      unaffected. Pass `true` whenever the tile will get a sparkline later.
 *  - reserveHint?: boolean — the same contract for the `hint` line (named
 *      `reserveSecondary` until 2026-08-05; renamed with the prop it guards).
 *      A slot that is reserved but never filled keeps its height (one
 *      non-breaking space), because a reserved slot collapsing on arrival is
 *      the shrink half of the same defect. 🔴 **2026-10-06 (E-W7):** the third
 *      line carries `mt: SP.chip` (loading branch too); the jump BETWEEN tiles
 *      with and without a hint is `KpiRow`'s cell alignment, not this.
 *  - valueComponent?: string — the ELEMENT the value renders as. Added
 *      2026-10-03 (bundle detail page, Rule-16 gate). The value is
 *      `<Typography variant="h6">`, and MUI maps that variant to an `<h6>` when
 *      no `component` is given — i.e. every KPI value was a HEADING to assistive
 *      tech ("Oversold by 3" and "Σ 25,00 €" announced as section titles, while
 *      the label above them is not one; WCAG 1.3.1).
 *      🔴 **2026-10-10 (K-1, UI-Durchgang 2 Teil 8): die VORGABE ist `p`.** Der
 *      Wert rendert als Text mit DERSELBEN Optik (`variant="h6"` bleibt, nur das
 *      Element wechselt). Der Prop ist damit nur noch der AUSWEG fuer einen
 *      anderen Elementnamen (etwa `"div"`); die 13 Aufrufer mit
 *      `valueComponent="p"` sind woertlich unveraendert. Die Umstellung der
 *      Vorgabe ist genau die repoweite Aenderung, die die Registry seit dem
 *      2026-10-03 als offen fuehrte — sie zieht jede Kennzahlenzeile des Hauses
 *      auf einmal.
 */

/*
 * 🔴 K-T1 (UI-Durchgang 2, Teil 6, 2026-10-09) — hier stand `useCountUp`.
 *
 * Ein 600-ms-`requestAnimationFrame`-Anstieg von 0 auf den Wert, gesteuert
 * ueber einen eigenen Boolean-Prop. Er ist **entfernt** (Plan §1 F-1 = A,
 * Orchestrator-Entscheid) — aus drei Gruenden, die alle drei schon gemessen
 * waren:
 *
 *  · **Er war ein zweites Bewegungs-Vokabular.** Die Hausregel fuer einen
 *    Kennzahlwechsel ist „sofort" — die Zahl ist eine Aussage, keine
 *    Ankunft. `RMAFaelligkeit.jsx` schaltete ihn ausdruecklich ab, mit der
 *    Begruendung, dass `useCountUp` bei 0 startet und der Wert damit erst nach
 *    600 ms im DOM steht.
 *  · **Er machte jede Probe zeitabhaengig.** `RmaOverviewKpiHeader.test.jsx`
 *    musste an ZWEI Stellen auf `findBy` ausweichen, weil der Endwert auf dem
 *    ersten synchronen Frame nicht stand. Zwei Zusicherungen, die ihren
 *    Gegenstand ueberleben, sobald der Anstieg faellt (Regel 15 §Rueckbau).
 *  · **Der Wert selbst war schon einmal falsch.** W-65 hat dokumentiert, dass
 *    der Anstieg bei jedem Wertwechsel von vorn lief: ein Refresh von 578 auf
 *    579 zeigte 578 → 0 → 579 und loeschte genau das Delta, das der Refresh
 *    sichtbar machen sollte. Repariert wurde damals mit einem `firstRunRef`;
 *    die Regel „eine Kennzahl springt rueckwaerts" blieb als Restrisiko.
 *
 * ⚠️ **Was NICHT mitgeht:** die Probe in `RmaOverviewKpiHeader.test.jsx`, die
 * den ANSTIEG selbst prueft, gibt es nicht — die zwei `findBy`-Stellen waren
 * Begruendungen, keine Zusicherungen auf die Dauer. Die Zusicherungen auf die
 * WERTE bleiben und sind ab jetzt sofort erfuellbar.
 */

/** Tones `scale="v2"` COLOURS. `plain` — the neutral default, and the same
 *  word `Pill` uses for its neutral — is deliberately absent: it means "no tone
 *  colour", so it falls through to `text.primary` like any unknown value. */
const V2_TONES = ['ok', 'warn', 'err', 'info'];

/** Sparkline row height. ONE constant, because the loading skeleton, the
 *  reserved-but-empty box and the real Sparkline must agree to the pixel —
 *  three literals is how the slot starts jumping again.
 *
 *  🔴 Exportiert seit Welle 4 PR 3, Nachbesserung 3 (2026-10-07): das Geruest
 *  von `ProductDetailSkeleton` reserviert dieselbe Zeile in seiner zweiten
 *  `KpiRow` (geladen traegt sie `series=…`). Ein zweites Literal dort waere
 *  genau der Fehler, den dieser Kommentar beschreibt — die Hoehe kommt aus
 *  DERSELBEN Quelle wie die Ladeform dieses Bausteins (`:286`). */
export const SERIES_H = 30;

/**
 * Design-Language v2 label/value anatomy. See the `scale` prop.
 *
 * 🔴 **2026-10-07 (UI-Durchgang 2): die BESCHRIFTUNG ist Satzschreibung, nicht
 *    mehr `uppercase` mit `.06em`-Sperrung.** Der Eigentuemer hat die Seite
 *    `/rma/faelligkeit` fotografiert — dort standen die Kachelbeschriftungen in
 *    Versalien, waehrend die Norm im Haus (`StatTile` in der Vorgabe-Skala,
 *    z. B. `ProductDetail.jsx` „Incoming", `MarketIntelligenceWidget.jsx`
 *    „Competitors") Satzschreibung ist. Zwei Beschriftungsstile nebeneinander
 *    fuer dieselbe Aussage.
 *
 *    Die v2-Skala bleibt sonst unveraendert: der WERT traegt weiter Plus Jakarta
 *    Sans 800 in `FS.display` mit `tabular-nums` (`v2ValueSx`) — DAS ist der
 *    Unterschied zur Vorgabe-Skala, nicht die Beschriftung.
 *
 *    Blast radius, gemessen: `scale="v2"` hat genau EINEN Produktionsaufrufer,
 *    `rma/RMAFaelligkeit.jsx` (zwei Stellen). `RmaOverviewKpiHeader` faehrt die
 *    Vorgabe-Skala.
 */
const v2LabelSx = (t) => ({
  fontSize: FS.xs,
  fontWeight: 600,
  color: v2Tone(t, 'ink').ink45,
});
const v2ValueSx = (t, tone) => ({
  fontFamily: '"Plus Jakarta Sans", sans-serif',
  fontWeight: 800,
  fontSize: FS.display,
  lineHeight: 1.15,
  // KPI surfaces poll; without tabular figures the numbers jump on refresh.
  fontVariantNumeric: 'tabular-nums',
  // Allow-list like Pill's and TierBadge's: v2Tone returns undefined for an
  // unknown key, so an unguarded `tone` would throw inside a shared component
  // during render.
  ...(V2_TONES.includes(tone) && { color: v2Tone(t, tone).main }),
});

export default function StatTile({ label, value, hint, series, right, labelTooltip, onClick, ariaLabel, valueColor, scale = 'default', tone = 'plain', loading = false, reserveSeries, reserveHint, valueComponent }) {
  const isV2 = scale === 'v2';
  // `isNumeric` survived the removal of the count-up (K-T1): the `aria-label`
  // below reads it, and the regex is the definition of "a plain number".
  const isNumeric = typeof value === 'number' || (typeof value === 'string' && /^\d+(\.\d+)?$/.test(String(value)));
  // Der Wert steht sofort. Hier stand bis K-T1 ein Anstiegs-Zweig
  // (`prop && isNumeric ? counted : (value ?? '—')`) — der Anstieg ist
  // entfernt, die Zusage der Komponente ist damit „der Wert, sobald er da ist"
  // (siehe die Notiz an der Stelle, an der `useCountUp` stand).
  const displayValue = value ?? '—';

  // Enter / Space activate the click handler (Space also prevents page scroll).
  const handleKeyDown = (e) => {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      onClick(e);
    }
  };

  // ── The two optional slots, resolved ONCE for both branches ───────────────
  // The loading branch cannot infer the tile's anatomy from the data, because
  // while loading there IS no data: the only real caller passes
  // `series={kpis.open?.series}` with `kpis === null`, i.e. `undefined`. A
  // loading branch keyed on `series` therefore reserved nothing and the tile
  // grew ~38px the moment the fetch landed — which cancels the very reason the
  // label is not skeletonised. Keying it on `series={[]}` inverts the bug into
  // a shrink. So the caller DECLARES the anatomy and both branches read the
  // same flag; the defaults reproduce the loaded branch's old condition
  // exactly, so no existing call site changes.
  const seriesHasData = Array.isArray(series) && series.length > 0;
  const showSeries = reserveSeries ?? seriesHasData;
  const showHint = reserveHint ?? (hint != null && hint !== '');

  // The label is a static prop, so it renders identically in both the loaded
  // and the loading form — that is what keeps the tile from jumping.
  //
  // 🔴 F5-10 (UI-Durchgang 2 Teil 5, 2026-10-09): das Etikett trug `noWrap` +
  //    Ellipsis, sein Volltext stand NUR im `Tooltip` — und der oeffnet auf
  //    Touch nicht. Auf dem Telefon war ein langes Etikett damit abgeschnitten
  //    UND unerreichbar. `KPI_LABEL_SX` ersetzt `noWrap` vollstaendig: schmal
  //    und auf grobem Zeiger bricht es um, ab `sm` bleibt es einzeilig mit
  //    Ellipsis. Dieselbe Form traegt `FulfillmentCostKpiCard`.
  const labelNode = labelTooltip ? (
    <Tooltip title={labelTooltip} arrow>
      <Typography variant="caption" color="text.secondary"
        sx={(t) => ({ ...KPI_LABEL_SX, cursor: 'help', ...(isV2 && v2LabelSx(t)) })}>
        {label}
      </Typography>
    </Tooltip>
  ) : (
    <Typography variant="caption" color="text.secondary"
      sx={(t) => ({ ...KPI_LABEL_SX, ...(isV2 && v2LabelSx(t)) })}>
      {label}
    </Typography>
  );

  if (loading) {
    return (
      // `aria-busy` marks this subtree as mid-update so AT can hold off on
      // reporting half-built content. It is NOT an announcement — it does not
      // make anything say "loading", and the earlier comment here claimed it
      // did. Nothing announces on purpose: a five-tile KPI row that each
      // announced its own load would be the same per-row tax ProvenanceMark's
      // inline mode exists to avoid. The label is already read; the value
      // arrives when it arrives.
      <Box aria-busy="true" sx={{ width: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {labelNode}
          {/* `right` is derived from the same fetch — a trend chip computed
              from data we do not have yet would be a fabrication. */}
        </Stack>
        <Skeleton
          variant="rounded"
          width="55%"
          height={isV2 ? FS.display : 24}
          sx={{ mt: SP.chip }}
        />
        {/* Both slots use the SAME `show*` flag as the loaded branch below —
            see the note on `reserveSeries`. A condition that differs between
            the two branches is a guaranteed jump at the moment data lands. */}
        {showHint && <Skeleton variant="text" width="40%" sx={{ mt: SP.chip }} />}
        {showSeries && (
          <Skeleton variant="rounded" height={SERIES_H} sx={{ mt: 1 }} />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {labelNode}
        {right}
      </Stack>
      <Typography
        variant="h6"
        // 🔴 K-1 (UI-Durchgang 2 Teil 8, 2026-10-10): die VORGABE ist `p`.
        // `variant="h6"` bleibt die OPTIK (Groesse, Gewicht), das ELEMENT ist
        // jetzt Text. Bis hierher bildete MUI die Variante ohne `component` auf
        // ein echtes `<h6>` ab — jede Kennzahl ohne `valueComponent` stand damit
        // als Ueberschrift im Baum der Hilfstechnik, waehrend ihre BESCHRIFTUNG
        // keine war (WCAG 1.3.1). `valueComponent` ist nur noch der Ausweg fuer
        // einen anderen Elementnamen. See the prop note above.
        component={valueComponent ?? 'p'}
        // `valueColor` WINS over `tone`. Two authorities over one property
        // without a stated precedence is the same defect as two theming
        // mechanisms — `tone` is the semantic default, `valueColor` the
        // documented escape hatch.
        color={valueColor || 'text.primary'}
        onClick={onClick}
        {...(onClick && {
          role: 'button',
          tabIndex: 0,
          // 🔴 W2 (Rule-16 gate, 2026-09-19): an `aria-label` REPLACES the
          // element's content, so a screen reader heard "Overdue cases, button"
          // and never the value. The value is now named when it is not a plain
          // number.
          //
          // ⚠️ Corrected 2026-09-20 (W-06 gate). This read "— which is exactly
          // when it is the unknown mark", and that is backwards. The condition
          // below is `isNumeric || value == null ? label : …`, so a `null` value
          // takes the `label` branch: the unknown mark is the one case that is
          // NOT named. Measured — `undefined`/`null`/`0`/`7` all give
          // `aria-label="Overdue cases"`; only a non-numeric non-null such as
          // `"1.2k"` is named. Still an improvement over never naming the value,
          // but the rationale has to match the code.
          'aria-label': ariaLabel || (typeof label === 'string'
            ? (isNumeric || value == null ? label : `${label}: ${value}`)
            : undefined),
          onKeyDown: handleKeyDown,
        })}
        sx={(t) => ({
          lineHeight: 1.25,
          mt: SP.chip,
          ...(isV2 && v2ValueSx(t, valueColor ? 'plain' : tone)),
          ...(onClick && {
            // inline-block so the underline + focus ring hug the value text,
            // not the full-width cell; and give a comfortable touch target.
            display: 'inline-block',
            cursor: 'pointer',
            // persistent at-rest affordance: dotted underline reads as interactive
            // without needing hover.
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
            textUnderlineOffset: '3px',
            textDecorationColor: 'currentColor',
            py: 0.25,
            borderRadius: 1,
            transition: 'opacity 0.15s, text-decoration-style 0.15s',
            // 🔴 K-H-Hausregel (`pattern-library.md`, K-5 der Partition): eine
            // WIRKUNG gehoert in `hoverOnly(...)`. Auf einem Touchgeraet gibt es
            // kein Hover — der Browser wendet `:hover` beim Tippen trotzdem an
            // und LAESST es stehen: die Unterstreichung bliebe solide und der
            // Wert gedimmt, bis man woanders hintippt. Der Fokusring daneben ist
            // keine Wirkung und bleibt nackt.
            ...hoverOnly({ textDecorationStyle: 'solid', opacity: 0.8 }),
            '&:focus-visible': focusRing(),
          }),
        })}
      >
        {displayValue}
      </Typography>
      {/* Same flags as the loading branch. When a slot is RESERVED but the
          data never arrived, the box still holds its height — a reserved slot
          that collapses on arrival is the shrink half of the same bug. The
          non-breaking space is what gives an empty caption a line box; an
          empty inline element has zero height. */}
      {showHint && (
        // The contract's third line. Under the v2 scale it takes `ink70` — the
        // canon's level for text that supports the value without competing with
        // it. Until 2026-08-05 this line rendered as `color="text.secondary"` in
        // BOTH scales, i.e. a v2 tile carried one line that had never been
        // through the v2 treatment at all. The default scale keeps
        // `text.secondary` on purpose: 107 call sites use it and none of them
        // opted into v2.
        <Typography
          variant="caption"
          {...(!isV2 && { color: 'text.secondary' })}
          // 🔴 E-W7: `display: 'block'` ist die tragende Hälfte. `caption` rendert
          // ein INLINE-`<span>`, auf dem `mt` nicht wirkt — neben dem klickbaren
          // Wert (inline-block) floss die Zeile auf derselben Zeile weiter.
          sx={(t) => ({ display: 'block', mt: SP.chip, ...(isV2 ? { color: v2Tone(t, 'ink').ink70 } : {}) })}
        >
          {hint != null && hint !== '' ? hint : ' '}
        </Typography>
      )}
      {showSeries && (
        <Box sx={{ mt: 1, height: SERIES_H }}>
          {seriesHasData && <Sparkline data={series} height={SERIES_H} />}
        </Box>
      )}
    </Box>
  );
}
